import { useMemo } from "react";
import AssignRewardFormDialog from "../AssignRewardFormDialog";
import CalendarEventDialog from "../CalendarEventDialog";
import ResetUserFormDialog from "../ResetUserFormDialog";
import LockUserFormDialog from "../LockUserFormDialog";
import InitiateUserFormDialog from "../InitiateUserFormDialog";
import type { UserInputMode } from "../../hooks/useUserProfileCard";

export interface UserActionDialogsProps {
  readonly item: Record<string, any> | null;
  readonly inputMode: UserInputMode;
  readonly inputOpen: boolean;
  /** Close without reloading (assign-reward / schedule / reset). */
  readonly onClose: () => void;
  /** Close AND reload the card (lock / confirm contact). */
  readonly onFinish: () => void;
}

/**
 * The shared bundle of per-user action dialogs. Every card renders all of them; only the one
 * matching the active `inputMode` opens, so this is safe to drop into any user-card view.
 */
function UserActionDialogs(props: UserActionDialogsProps) {
  const { item, inputMode, inputOpen, onClose, onFinish } = props;

  // S2: stable empty-initialValues reference. CalendarEventDialog's useEffect re-fires on identity change
  // of `initialValues`, so a fresh `{}` on every parent re-render used to wipe anything the user had
  // started typing (title, times, etc). One shared frozen object is enough — nothing writes to it.
  const emptyInitial = useMemo(() => ({}) as any, []);

  return (
    <>
      <AssignRewardFormDialog
        open={inputOpen && inputMode === "assignReward"}
        initialValues={emptyInitial}
        target={{ type: "user", uid: item?.uid! }}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <CalendarEventDialog
        mode={"add"}
        open={inputOpen && inputMode === "scheduleEvent"}
        initialValues={emptyInitial}
        target={{ type: "user", uid: item?.uid! }}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <ResetUserFormDialog
        open={inputOpen && inputMode === "resetPassword"}
        initialValues={{ userUid: item?.uid!, userName: item?.userName! }}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <LockUserFormDialog
        open={inputOpen && ["lock", "unlock"].includes(inputMode)}
        initialValues={{
          userUid: item?.uid!,
          userName: item?.userName!,
          lockoutEnd: item?.lockoutEnd,
          lockoutEnabled: item?.lockoutEnabled,
        }}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <InitiateUserFormDialog
        open={
          inputOpen &&
          ["confirmEmail", "confirmPhone", "confirmCompanyPhone"].includes(inputMode)
        }
        type={inputMode as any}
        initialValues={{ userUid: item?.uid!, userName: item?.userName! }}
        onFinish={onFinish}
        onCancel={onClose}
      />
    </>
  );
}

export default UserActionDialogs;
